import {
  Box,
  ButtonBase,
  InputBase,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { enqueueSnackbar } from "notistack";
import { Button, CurrencyIcon, Skeleton, FeatureGuard } from "../../components/shared";
import {
  DEPOSIT_NETWORKS,
  fetchNetworks,
  getNetworkById,
} from "../../components/pages/wallet/networks";
import { submitCryptoWithdraw } from "../../services/crypto";
import { getUserInfo } from "../../services/me";
import { useTranslation } from "react-i18next";

function parseAmount(raw: string): number {
  const cleaned = raw.replace(",", ".").replace(/[^0-9.]/g, "");
  const value = parseFloat(cleaned);
  return Number.isFinite(value) ? value : 0;
}

function parseFeeUsdt(raw: string): number {
  const match = raw.match(/([\d.]+)/);
  return match ? parseFloat(match[1]) : 0;
}

function formatUsdt(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0";
  return value % 1 === 0 ? value.toString() : value.toFixed(2);
}

function NetworkBadge({
  logoUrl,
  label,
}: {
  logoUrl: string;
  label: string;
}) {
  return (
    <Box
      sx={(theme) => ({
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        padding: "6px 14px 6px 6px",
        borderRadius: "999px",
        bgcolor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.06)"
            : "rgba(0, 0, 0, 0.06)",
      })}
    >
      <CurrencyIcon value={logoUrl} size={22} fallbackLetter={label} alt={label} />
      <Typography
        sx={{
          fontSize: "13px",
          fontWeight: 500,
          color: "text.primary",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function InfoRow({
  label,
  value,
  valueColor,
}: {
  label: string;
  value: string;
  valueColor?: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 16px",
        gap: "12px",
      }}
    >
      <Typography
        sx={{
          fontSize: "14px",
          color: "text.secondary",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 500,
          color: valueColor ?? "text.primary",
          textAlign: "right",
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function WithdrawSkeleton() {
  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "10px",
          marginBottom: "8px",
        }}
      >
        <Skeleton
          variant="rounded"
          width={200}
          height={26}
          sx={{ borderRadius: "8px" }}
        />
        <Skeleton
          variant="rounded"
          width={160}
          height={30}
          sx={{ borderRadius: "999px" }}
        />
      </Box>

      <Skeleton variant="rounded" height={110} sx={{ borderRadius: "18px" }} />
      <Skeleton variant="rounded" height={90} sx={{ borderRadius: "18px" }} />
      <Skeleton variant="rounded" height={130} sx={{ borderRadius: "18px" }} />
      <Skeleton
        variant="rounded"
        height={44}
        sx={{ borderRadius: "40px", marginTop: "8px" }}
      />
    </>
  );
}

function WithdrawPageContent() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation("withdraw");
  const { data: networks, isLoading: networksLoading } = useQuery({
    queryFn: fetchNetworks,
    queryKey: ["networks"],
    staleTime: 60_000,
  });
  const list = networks && networks.length > 0 ? networks : DEPOSIT_NETWORKS;
  const network = useMemo(
    () =>
      getNetworkById(
        searchParams.get("network"),
        list,
        searchParams.get("currency"),
      ),
    [searchParams, list],
  );

  const { data: user, isLoading: userLoading } = useQuery({
    queryFn: getUserInfo,
    queryKey: ["me"],
  });
  const isLoading = networksLoading || userLoading;

  const currentBalance = Number(
    user?.balances?.find((b) => b.currency === network.currency)?.amount ?? 0,
  );

  const [amount, setAmount] = useState("");
  const [address, setAddress] = useState("");

  const amountNum = parseAmount(amount);
  const networkFee = parseFeeUsdt(network.platformFee);
  const minAmount = parseFeeUsdt(network.minAmount);
  const receiveAmount = Math.max(0, amountNum - networkFee);

  const amountError =
    amount && amountNum > 0 && amountNum < minAmount
      ? t("min_amount", { amount: network.minAmount })
      : amountNum > currentBalance
      ? t("insufficient")
      : "";

  const isValid =
    amountNum >= minAmount &&
    amountNum <= currentBalance &&
    address.trim().length >= 20 &&
    !amountError;

  const handleAmountChange = (raw: string) => {
    let cleaned = raw.replace(",", ".").replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    if (parts.length > 2) cleaned = parts[0] + "." + parts.slice(1).join("");
    setAmount(cleaned);
  };

  const handleMax = () => {
    if (currentBalance > 0) setAmount(String(currentBalance));
    else enqueueSnackbar(t("toasts.no_balance"), { variant: "info" });
  };

  const queryClient = useQueryClient();
  const { mutate: submitWithdraw, isLoading: submitting } = useMutation({
    mutationFn: () =>
      submitCryptoWithdraw({
        networkCode: network.id,
        currencyCode: network.currency,
        addressTo: address.trim(),
        amount: String(amountNum),
      }),
    onSuccess: () => {
      enqueueSnackbar(t("success"), { variant: "success" });
      setAmount("");
      setAddress("");
      // Освежаем баланс и историю — новый вывод отразится сразу.
      queryClient.invalidateQueries("me");
      queryClient.invalidateQueries("crypto-overview");
      queryClient.invalidateQueries("crypto-transactions");
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.message || t("toasts.submit_failed");
      enqueueSnackbar(String(msg), { variant: "error" });
    },
  });

  const handleSubmit = () => submitWithdraw();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        width: "100%",
        marginTop: "20px",
        marginBottom: "140px",
      }}
    >
      {isLoading ? (
        <WithdrawSkeleton />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
              marginBottom: "6px",
            }}
          >
            <Typography
              sx={{
                fontSize: "22px",
                fontWeight: 600,
                textAlign: "center",
              }}
            >
              {t("title")}
            </Typography>
            <NetworkBadge
              logoUrl={network.logoUrl}
              label={network.fullName}
            />
          </Box>

          {/* Amount */}
          <Box
            sx={{
              borderRadius: "18px",
              bgcolor: "secondary.main",
              padding: "14px 16px 12px",
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                color: "text.secondary",
                marginBottom: "6px",
              }}
            >
              {t("amount")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <InputBase
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                placeholder="0"
                inputMode="decimal"
                sx={(theme) => ({
                  flex: 1,
                  fontSize: "32px",
                  fontWeight: 600,
                  color: amountError ? "#FF3C14" : theme.palette.text.primary,
                  padding: 0,
                  "& input": {
                    padding: 0,
                    "&::placeholder": {
                      color:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.3)"
                          : "rgba(0,0,0,0.3)",
                      opacity: 1,
                    },
                  },
                })}
              />
              <Typography
                sx={{
                  fontSize: "22px",
                  fontWeight: 600,
                  color: "text.secondary",
                }}
              >
                {network.currency}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: "8px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  color: amountError ? "#FF3C14" : "text.secondary",
                }}
              >
                {amountError ||
                  t("amount_available", {
                    amount: formatUsdt(currentBalance),
                    code: network.currency,
                  })}
              </Typography>
              <ButtonBase
                onClick={handleMax}
                sx={{
                  padding: "4px 10px",
                  borderRadius: "10px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "primary.main",
                  bgcolor: "rgba(51, 119, 255, 0.15)",
                }}
              >
                {t("max")}
              </ButtonBase>
            </Box>
          </Box>

          {/* Address */}
          <Box
            sx={{
              borderRadius: "18px",
              bgcolor: "secondary.main",
              padding: "14px 16px",
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                color: "text.secondary",
                marginBottom: "6px",
              }}
            >
              {t("to_address")}
            </Typography>
            <TextField
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder={t("address_placeholder", {
                network: network.standard,
              })}
              variant="standard"
              fullWidth
              multiline
              maxRows={3}
              slotProps={{
                input: {
                  disableUnderline: true,
                  sx: {
                    padding: 0,
                    fontSize: "15px",
                    color: "text.primary",
                    "&::before, &::after": { display: "none" },
                  },
                },
              }}
              sx={(theme) => ({
                "& textarea::placeholder": {
                  color:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.3)"
                      : "rgba(0, 0, 0, 0.3)",
                  opacity: 1,
                },
                "& textarea": {
                  wordBreak: "break-all",
                },
              })}
            />
          </Box>

          {/* Fee summary */}
          <Box
            sx={{
              borderRadius: "18px",
              bgcolor: "secondary.main",
              overflow: "hidden",
              "& > *:not(:last-child)": {
                borderBottom: "1px solid",
                borderColor: "divider",
              },
            }}
          >
            <InfoRow label={t("network_label")} value={network.fullName} />
            <InfoRow
              label={t("network_fee_label")}
              value={network.platformFee}
            />
            <InfoRow
              label={t("receive_label")}
              value={`${formatUsdt(receiveAmount)} ${network.currency}`}
              valueColor="#00CA48"
            />
          </Box>

          <Button
            sx={{ marginTop: "8px" }}
            disabled={!isValid || submitting}
            onClick={handleSubmit}
          >
            {submitting ? t("confirming") : t("confirm")}
          </Button>
        </>
      )}
    </Box>
  );
}

export function WithdrawPage() {
  return (
    <FeatureGuard feature="withdraw">
      <WithdrawPageContent />
    </FeatureGuard>
  );
}
